import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const SiteLinks = ({ site }) => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  // Состояние для данных
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLinks, setTotalLinks] = useState(0);
  const [limit] = useState(50); // 50 ссылок на страницу
  
  // Функции для работы с датами
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(startOfMonth),
      to: formatDate(endOfMonth)
    };
  };

  const getLastMonthRange = () => {
    const now = new Date();
    const lastMonth = now.getMonth() - 1;
    const year = lastMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = lastMonth < 0 ? 11 : lastMonth;
    
    const startOfLastMonth = new Date(year, month, 1);
    const endOfLastMonth = new Date(year, month + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(startOfLastMonth),
      to: formatDate(endOfLastMonth)
    };
  };

  // Состояние для фильтров
  const [filters, setFilters] = useState(() => {
    const currentMonth = getCurrentMonthRange();
    return {
      dat_from: currentMonth.from, // дата создания с
      dat_to: currentMonth.to, // дата создания по
      status: '', // статус
    };
  });
  
  // Состояние для сортировки
  const [sortField, setSortField] = useState('dat');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Состояние для индикаторов загрузки
  const [checkingLinks, setCheckingLinks] = useState(new Set());
  
  // Состояние для раскрытия фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Загрузка данных
  const fetchLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      // Формируем параметры запроса
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
        site_id: site.id.toString(), // Фильтр по ID сайта
        aurl: site.dns, // URL акцептора - текущий домен сайта
      });
      
      // Добавляем фильтры, если они заполнены
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const stringValue = typeof value === 'string' ? value.trim() : String(value);
          if (stringValue !== '') {
            switch (key) {
              case 'status':
                if (stringValue !== '') {
                  const statusValue = parseInt(stringValue);
                  if (!isNaN(statusValue)) {
                    params.append('status', statusValue.toString());
                  }
                }
                break;
              default:
                params.append(key, stringValue);
                break;
            }
          }
        }
      });
      
      const url = `${API_URL}${currentLang}/links?${params.toString()}`;
      
      const response = await fetchWithAuth(url, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setLinks(data.links || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalLinks(data.pagination?.total || 0);
      } else {
        throw new Error(data.message || 'Ошибка загрузки ссылок');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Загружаем данные при изменении параметров
  useEffect(() => {
    if (site?.id) {
      fetchLinks();
    }
  }, [currentPage, sortField, sortDirection, filters, site?.id]);
  
  // Автоматически раскрываем фильтры при наличии активных фильтров
  useEffect(() => {
    if (getActiveFiltersCount() > 0 && !filtersExpanded) {
      setFiltersExpanded(true);
    }
  }, [filters]);
  
  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };
  
  // Обработчик сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };
  
  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true; // Для числовых значений
    }).length;
  };
  
  // Очистка фильтров
  const clearFilters = () => {
    const currentMonth = getCurrentMonthRange();
    setFilters({
      dat_from: currentMonth.from,
      dat_to: currentMonth.to,
      status: '',
    });
    setCurrentPage(1);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(dateString).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };
  
  // Обрезка URL для отображения
  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return '-';
    return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
  };
  
  // Получение иконки сортировки
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };
  
  // Проверка ссылки
  const handleCheckLink = async (linkId) => {
    try {
      setCheckingLinks(prev => new Set(prev).add(linkId));
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/links/${linkId}/check`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showNotification(currentLang === 'ru' ? 'Ссылка проверена!' : 'Link checked!', 'success');
        fetchLinks(); // Обновляем данные
      } else {
        throw new Error(data.message || 'Ошибка проверки ссылки');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        showNotification('Сессия истекла. Пожалуйста, войдите снова.', 'error');
      } else {
        showNotification(error.message, 'error');
      }
    } finally {
      setCheckingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
    }
  };
  
  // Показ уведомлений
  const showNotification = (message, type = 'success') => {
    // Здесь можно добавить систему уведомлений
  };
  
  // Копирование в буфер обмена
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(currentLang === 'ru' ? 'Ссылка скопирована!' : 'Link copied!', 'success');
    } catch (error) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showNotification(currentLang === 'ru' ? 'Ссылка скопирована!' : 'Link copied!', 'success');
      } catch (fallbackError) {
        showNotification(currentLang === 'ru' ? 'Ошибка копирования' : 'Copy failed', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md dark:bg-navy-700 dark:text-white dark:border-white/10">
      {/* Заголовок */}
      <div className="flex justify-between items-center mb-6 p-4 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold">{currentLang === 'ru' ? 'Размещенные ссылки' : 'Site Links'}</h3>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
              {getActiveFiltersCount()} {currentLang === 'ru' ? 'фильтр' : 'filter'}
              {getActiveFiltersCount() > 1 && (currentLang === 'ru' ? 'а' : 's')}
            </span>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchLinks}
            disabled={loading}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10">
        {/* Заголовок фильтров */}
        <div 
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-600 rounded-lg p-2"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h4 className="text-md font-medium">{currentLang === 'ru' ? 'Фильтры' : 'Filters'}</h4>

            </div>
            <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFilters();
                  }}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                >
                  {currentLang === 'ru' ? 'Очистить' : 'Clear'}
                </button>

              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Фильтры по статусу */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('status', '');
                }}
                className={`px-3 py-1 text-xs rounded-full border ${
                  filters.status === '' 
                    ? 'bg-green-500 text-white border-green-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Все' : 'All'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('status', '1');
                }}
                className={`px-3 py-1 text-xs rounded-full border ${
                  filters.status === '1' 
                    ? 'bg-green-500 text-white border-green-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Активные' : 'Active'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('status', '0');
                }}
                className={`px-3 py-1 text-xs rounded-full border ${
                  filters.status === '0' 
                    ? 'bg-yellow-500 text-white border-yellow-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Ожидают проверки' : 'Pending'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('status', '2');
                }}
                className={`px-3 py-1 text-xs rounded-full border ${
                  filters.status === '2' 
                    ? 'bg-red-500 text-white border-red-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('status', '99');
                }}
                className={`px-3 py-1 text-xs rounded-full border ${
                  filters.status === '99' 
                    ? 'bg-gray-300 text-gray-800' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Сняты' : 'Removed'}
              </button>
            </div>

            {/* Фильтры по датам */}
            <div className="flex flex-wrap gap-2 border-l border-gray-300 pl-2">
              {(() => {
                const currentMonth = getCurrentMonthRange();
                const lastMonth = getLastMonthRange();
                
                return (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('dat_from', currentMonth.from);
                        handleFilterChange('dat_to', currentMonth.to);
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.dat_from === currentMonth.from && filters.dat_to === currentMonth.to
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Этот месяц' : 'This month'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('dat_from', lastMonth.from);
                        handleFilterChange('dat_to', lastMonth.to);
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.dat_from === lastMonth.from && filters.dat_to === lastMonth.to
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Прошлый месяц' : 'Last month'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('dat_from', '');
                        handleFilterChange('dat_to', '');
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.dat_from === '' && filters.dat_to === ''
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Все даты' : 'All dates'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Раскрывающиеся фильтры */}
        {filtersExpanded && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Дата создания с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата создания с' : 'Created from'}
                </label>
                <input
                  type="date"
                  value={filters.dat_from}
                  onChange={(e) => handleFilterChange('dat_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
              
              {/* Дата создания по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата создания по' : 'Created to'}
                </label>
                <input
                  type="date"
                  value={filters.dat_to}
                  onChange={(e) => handleFilterChange('dat_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Таблица */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-red-600 text-center">{error}</div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-white/10">
              <thead className="bg-gray-50 dark:bg-navy-600">
                <tr>
                  <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-white">
                    {currentLang === 'ru' ? 'Действия' : 'Actions'}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('status')}
                  >
                    {currentLang === 'ru' ? 'Статус' : 'Status'} {getSortIcon('status')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('dat')}
                  >
                    {currentLang === 'ru' ? 'Дата создания' : 'Created'} {getSortIcon('dat')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('durl')}
                  >
                    {currentLang === 'ru' ? 'URL донора' : 'Donor URL'} {getSortIcon('durl')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('aurl')}
                  >
                    {currentLang === 'ru' ? 'URL акцептора' : 'Acceptor URL'} {getSortIcon('aurl')}
                  </th>
                  <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-600 dark:text-white">
                    {currentLang === 'ru' ? 'SEO параметры' : 'SEO Parameters'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-700 dark:divide-white/10">
                {links.map((link) => (
                  <tr key={link.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="p-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleCheckLink(link.id)}
                          disabled={checkingLinks.has(link.id)}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md disabled:opacity-50"
                          title={currentLang === 'ru' ? 'Проверить' : 'Check'}
                        >
                          {checkingLinks.has(link.id) ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          ) : (() => {
                            // Проверяем, была ли проверка сегодня
                            const today = new Date().toISOString().split('T')[0];
                            const lastCheckDate = link.dend;
                            const isCheckedToday = lastCheckDate === today;
                            
                            if (isCheckedToday) {
                              // Если проверяли сегодня - показываем галочку
                              return (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              );
                            } else {
                              // Если не проверяли сегодня - показываем иконку обновления
                              return (
                                <svg className="w-4 h-4" fill="none" stroke="gray" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              );
                            }
                          })()}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                        link.status === 1 ? 'bg-green-100 text-green-800' :
                        link.status === 0 ? 'bg-yellow-100 text-yellow-800' :
                        link.status === 2 ? 'bg-red-100 text-red-800' :
                        link.status === 99 ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {link.status === 0 ? (currentLang === 'ru' ? 'Ожидает проверки' : 'Pending') :
                         link.status === 1 ? (currentLang === 'ru' ? 'Активная' : 'Active') :
                         link.status === 2 ? (currentLang === 'ru' ? 'Не найдена' : 'Not found') :
                         link.status === 99 ? (currentLang === 'ru' ? 'Снята' : 'Removed') :
                         '-'}
                      </span>
                      <div className='mt-1'>{formatDate(link.dend)}</div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div>{formatDate(link.dat)}</div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => copyToClipboard(link.durl)}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                          title={currentLang === 'ru' ? 'Копировать ссылку' : 'Copy link'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <div className="max-w-xs flex-1">
                          <a 
                            href={link.durl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 truncate block"
                            title={link.durl}
                          >
                            {truncateUrl(link.durl)}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => copyToClipboard(link.aurl)}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                          title={currentLang === 'ru' ? 'Копировать ссылку' : 'Copy link'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <div className="max-w-xs flex-1">
                          <a 
                            href={link.aurl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 truncate block"
                            title={link.aurl}
                          >
                            {truncateUrl(link.aurl)}
                          </a>
                        </div>
                      </div>
                      {link.text && <div><span className="px-2 py-0 text-xs font-medium bg-green-100 text-black rounded-md"># {link.text} #</span></div>} 
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">
                            {currentLang === 'ru' ? 'Язык:' : 'Lang:'}
                          </span>
                          <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                            link.lang === 'ru' ? 'bg-blue-100 text-blue-800' :
                            link.lang === 'en' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {link.lang?.toUpperCase() || '-'}
                          </span>
                        </div>
                        
                        {/* Количество ссылок */}
                        {link.stat_links && link.stat_links > 0 ? (
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">
                              {currentLang === 'ru' ? 'Ссылки:' : 'Links:'}
                            </span>
                            <span className="px-1 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                              {link.stat_links}
                            </span>
                          </div>
                        ) : ''}
                        
                        {/* Позиция */}
                        {link.stat_pos && link.stat_pos != 'unknown' && (
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">
                              {currentLang === 'ru' ? 'Позиция:' : 'Pos:'}
                            </span>
                            <span className="px-1 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              {link.stat_pos}
                            </span>
                          </div>
                        )}
                        
                        {/* Атрибуты */}
                        {link.stat_attr && link.stat_attr != '' && (
                          <div className="flex items-start space-x-1">
                            <span className="text-xs text-gray-500 mt-0.5">
                              {currentLang === 'ru' ? 'Атрибуты:' : 'Attr:'}
                            </span>
                            <div className="flex flex-wrap gap-0.5">
                              {link.stat_attr.split(',').map((attr, index) => (
                                <span
                                  key={index}
                                  className="px-1 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded"
                                >
                                  {attr.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 dark:bg-navy-700 dark:border-white/10">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                  >
                    {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                  >
                    {currentLang === 'ru' ? 'Следующая' : 'Next'}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-white">
                      {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalLinks)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalLinks}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                      >
                        ←
                      </button>
                      
                      {/* Номера страниц */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-navy-600 dark:border-white/10 dark:text-white'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Сообщение об отсутствии данных */}
      {!loading && links.length === 0 && !error && (
        <div className="text-center py-8 text-gray-500 dark:text-white">
          {currentLang === 'ru' ? 'Ссылки не найдены' : 'Links not found'}
        </div>
      )}
    </div>
  );
};

export default SiteLinks; 